import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_6a9ec03d-39e5-4689-929c-7a68b7c62383/artifacts/7bwa7zl1_Yantraksh_Logo.png";

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      setDone(true);
      toast.success("Account created — pending admin approval.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4 py-10" data-testid="register-page">
      <div className="w-full max-w-md bg-white p-8 shadow-2xl border-t-4 border-gold">
        <Link to="/" className="flex justify-center mb-6">
          <img src={LOGO} alt="Yantraksh Projects" className="h-20 w-auto object-contain" />
        </Link>

        {done ? (
          <div className="text-center space-y-4" data-testid="register-success">
            <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto" />
            <h2 className="text-xl font-display font-bold">Account Created</h2>
            <p className="text-gray-600 text-sm">Your access is <span className="font-bold">pending admin approval</span>. You'll be able to sign in once an administrator approves your account.</p>
            <Button onClick={() => navigate("/login")} className="rounded-none uppercase tracking-widest">Go to Sign In</Button>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-display font-bold text-center text-foreground mb-1">Create Account</h1>
            <p className="text-center text-sm text-gray-500 mb-6">Registration requires admin approval</p>

            {error && <div data-testid="register-error" className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input id="name" data-testid="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-none" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input id="company" data-testid="register-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-none" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" data-testid="register-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="rounded-none" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" data-testid="register-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" data-testid="register-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="rounded-none" />
              </div>
              <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full rounded-none uppercase tracking-widest">
                {loading ? "Creating..." : "Sign Up"}
              </Button>
            </form>

            <p className="text-center text-sm text-gray-500 mt-6">
              Already have an account? <Link to="/login" className="text-primary font-bold hover:underline">Sign In</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Register;
